import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from "react";
import { WithAuth } from "../../components/with-auth";
import MainWrapper from "../../layout/MainWrapper";
import { Col, Row, Empty, Spin, Tooltip, Button, Modal } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { createAxiosInstance } from "../../api/ApiRequest";
import { useNavigate } from "react-router-dom";
import { notifyError, notifySuccess } from "../../utils/toast";
import { useServerContext } from "../../context/ServerContext";
import { useAuth } from "../../context/AuthContext";
import { Pm2LogMessage, Pm2ServiceLogPublic, ServerType } from "../../types/botServer";

type LogPaneType = ServerType | 'pm2_service';

const MAX_LOG_LENGTH = 150;
const INITIAL_LOG_LINES = 100;
const LIVE_THRESHOLD_MS = 30_000;
const PAGE_PADDING = 16;
const GRID_GAP = 12;
const PANE_HEIGHT = 400;

const TERMINAL = {
    bg: '#0d1117',
    border: '#30363d',
    text: '#c9d1d9',
    muted: '#8b949e',
    out: '#3fb950',
    err: '#f85149',
    accentMain: '#1f6feb',
    accentWl: '#8957e5',
    accentService: '#f0883e',
};

interface ColorMap {
    [key: string]: string;
}

interface ProcessKey {
    key: string;
    serverId: string;
    serverType: LogPaneType;
    processName: string;
    paneIndex: number;
}

interface LogEntry extends Pm2LogMessage {
    id: string;
}

function formatLogTime(timestamp: number): string {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function parseANSI(text: string): React.ReactNode[] {
    const colorMap: ColorMap = {
        '31': TERMINAL.err,
        '32': TERMINAL.out,
        '34': '#58a6ff',
        '36': '#39c5cf',
        '33': '#d29922',
        '39': TERMINAL.text,
    };
    const parts = text.split(/(\x1B\[\d+m|\[[\d:\.]+\])/);
    let currentColor = TERMINAL.text;

    return parts.map((part, index) => {
        if (part.match(/^\[[\d:\.]+\]$/)) {
            return <span key={index} style={{ color: TERMINAL.muted }}>{part}</span>;
        }
        const colorMatch = part.match(/\x1B\[(\d+)m/);
        if (colorMatch) {
            currentColor = colorMap[colorMatch[1]] || TERMINAL.text;
            return null;
        }
        if (!part) return null;
        return <span key={index} style={{ color: currentColor }}>{part}</span>;
    }).filter(Boolean);
}

function getAccentColor(serverType: LogPaneType): string {
    if (serverType === 'main_bot') return TERMINAL.accentMain;
    if (serverType === 'pm2_service') return TERMINAL.accentService;
    return TERMINAL.accentWl;
}

function isScrolledToBottom(el: HTMLElement, threshold = 8): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
}

const LiveIndicator: React.FC<{ lastTimestamp?: number }> = ({ lastTimestamp }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 5000);
        return () => clearInterval(timer);
    }, []);

    const isLive = Boolean(lastTimestamp && now - lastTimestamp < LIVE_THRESHOLD_MS);

    return (
        <span
            style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: isLive ? TERMINAL.out : '#484f58',
                marginRight: 6,
                verticalAlign: 'middle',
            }}
        />
    );
};

interface LogPaneProps {
    item: ProcessKey;
    entries: LogEntry[];
    logRef: (el: HTMLPreElement | null) => void;
    onLogScroll: () => void;
    onLogWheel: (event: React.WheelEvent<HTMLPreElement>) => void;
    onRestart: (serverId: string, processName: string) => void;
}

const LogPane = React.memo<LogPaneProps>(({ item, entries, logRef, onLogScroll, onLogWheel, onRestart }) => {
    const last = entries[entries.length - 1];
    const accent = getAccentColor(item.serverType);

    return (
        <div
            style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${TERMINAL.border}`,
                borderLeft: `3px solid ${accent}`,
                borderRadius: 6,
                background: TERMINAL.bg,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '3px 8px',
                    background: '#161b22',
                    borderBottom: `1px solid ${TERMINAL.border}`,
                    flexShrink: 0,
                }}
            >
                <span style={{ color: '#e6edf3', fontSize: 11, fontFamily: 'monospace' }}>
                    <LiveIndicator lastTimestamp={last?.timestamp} />
                    #{item.paneIndex} {item.serverId}
                    {item.serverType === 'pm2_service' && (
                        <span style={{ color: TERMINAL.muted, marginLeft: 6 }}>(service)</span>
                    )}
                </span>
                <span>
                    <Tooltip title="Restart">
                        <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined style={{ fontSize: 12 }} />}
                            style={{ color: TERMINAL.muted, width: 24, height: 24 }}
                            onClick={() => onRestart(item.serverId, item.processName)}
                        />
                    </Tooltip>
                </span>
            </div>
            <pre
                ref={logRef}
                onScroll={onLogScroll}
                onWheel={onLogWheel}
                className="bot-log-scroll"
                style={{
                    flex: 1,
                    minHeight: 0,
                    margin: 0,
                    padding: '6px 8px',
                    background: TERMINAL.bg,
                    color: TERMINAL.text,
                    overflowY: 'auto',
                    overflowAnchor: 'none',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: "'Cascadia Code', 'Consolas', monospace",
                    fontSize: 11,
                }}
            >
                {renderLogsForKey(entries)}
            </pre>
        </div>
    );
});

function renderLogsForKey(entries: LogEntry[]) {
    if (entries.length === 0) {
        return <span style={{ color: TERMINAL.muted }}>—</span>;
    }

    return entries.flatMap((log) => {
        const lines = log.message.split('\n');
        if (lines.length > 0) lines.pop();
        return lines.map((line, li) => (
            <div key={`${log.id}-${li}`} style={{ lineHeight: 1.4 }}>
                <span style={{ color: TERMINAL.muted }}>{formatLogTime(log.timestamp)} </span>
                <span style={{ color: log.stream === 'err' ? TERMINAL.err : TERMINAL.out }}>
                    {log.stream === 'err' ? '!' : '›'}
                </span>{' '}
                {parseANSI(line)}
            </div>
        ));
    });
}

const BotLog: React.FC = () => {
    const history = useNavigate();
    const { logout } = useAuth();
    const axiosInstance = useMemo(() => createAxiosInstance(history, logout), [history, logout]);
    const { servers, loading: configLoading, authenticatedWebSocketUrl } = useServerContext();
    const [pm2Services, setPm2Services] = useState<Pm2ServiceLogPublic[]>([]);
    const [pm2Loading, setPm2Loading] = useState(true);
    const [logs, setLogs] = useState<Record<string, LogEntry[]>>({});
    const logIdCounter = useRef(0);
    const logRefs = useRef<Record<string, HTMLPreElement | null>>({});
    const pinnedToBottom = useRef<Record<string, boolean>>({});
    const pendingScrollKeys = useRef<Set<string>>(new Set());
    const scrollSnapshots = useRef<Record<string, { scrollTop: number; scrollHeight: number }>>({});
    const mountedPanes = useRef<Set<string>>(new Set());
    const loadedLogKeysRef = useRef<Set<string>>(new Set());

    const isPinned = useCallback((key: string) => pinnedToBottom.current[key] === true, []);

    const setPinned = useCallback((key: string, pinned: boolean) => {
        pinnedToBottom.current[key] = pinned;
    }, []);

    const captureScrollSnapshot = useCallback((key: string) => {
        const el = logRefs.current[key];
        if (!el || isPinned(key)) return;
        scrollSnapshots.current[key] = {
            scrollTop: el.scrollTop,
            scrollHeight: el.scrollHeight,
        };
    }, [isPinned]);

    const toLogEntry = useCallback((log: Pm2LogMessage): LogEntry => {
        logIdCounter.current += 1;
        return {
            ...log,
            id: `${log.serverId}-${log.timestamp}-${logIdCounter.current}`,
        };
    }, []);

    const trimLogs = useCallback((entries: LogEntry[], pinned: boolean): LogEntry[] => {
        if (entries.length <= MAX_LOG_LENGTH) return entries;
        if (!pinned) return entries;
        return entries.slice(-MAX_LOG_LENGTH);
    }, []);

    const trimLogsForKey = useCallback((key: string) => {
        setLogs((prev) => {
            const existing = prev[key];
            if (!existing || existing.length <= MAX_LOG_LENGTH) return prev;
            const trimmed = existing.slice(-MAX_LOG_LENGTH);
            if (trimmed.length === existing.length) return prev;
            pendingScrollKeys.current.add(key);
            return { ...prev, [key]: trimmed };
        });
    }, []);

    const processKeys = useMemo(() => {
        const keys: ProcessKey[] = [];
        let paneIndex = 0;
        for (const server of servers) {
            paneIndex += 1;
            keys.push({
                key: server.serverId,
                serverId: server.serverId,
                serverType: server.type,
                processName: server.serverId,
                paneIndex,
            });
        }
        for (const service of pm2Services) {
            paneIndex += 1;
            keys.push({
                key: service.processName,
                serverId: service.processName,
                serverType: 'pm2_service',
                processName: service.processName,
                paneIndex,
            });
        }
        return keys;
    }, [servers, pm2Services]);

    const processKeysSignature = useMemo(
        () => processKeys.map((item) => `${item.serverId}:${item.processName}`).join('|'),
        [processKeys],
    );

    const scrollPaneToBottom = useCallback((key: string) => {
        const el = logRefs.current[key];
        if (!el || !isPinned(key)) return;
        el.scrollTop = el.scrollHeight;
    }, [isPinned]);

    const appendLog = useCallback((log: Pm2LogMessage) => {
        const key = log.serverId;
        if (pinnedToBottom.current[key] === undefined) {
            pinnedToBottom.current[key] = true;
        }

        captureScrollSnapshot(key);

        if (isPinned(key)) {
            pendingScrollKeys.current.add(key);
        }

        setLogs((prev) => {
            const existing = prev[key] ?? [];
            const next = [...existing, toLogEntry(log)];
            return { ...prev, [key]: trimLogs(next, isPinned(key)) };
        });
    }, [captureScrollSnapshot, isPinned, toLogEntry, trimLogs]);

    useEffect(() => {
        let cancelled = false;

        const fetchPm2Services = async () => {
            setPm2Loading(true);
            try {
                const result = await axiosInstance.post<{
                    flag: boolean;
                    services: Pm2ServiceLogPublic[];
                }>('/api/v1/get_pm2_service_logs');
                if (!cancelled && result.data?.flag) {
                    setPm2Services(result.data.services ?? []);
                }
            } catch (error) {
                console.error('Failed to load PM2 services:', error);
            } finally {
                if (!cancelled) setPm2Loading(false);
            }
        };

        fetchPm2Services();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance]);

    useEffect(() => {
        if (configLoading || pm2Loading || processKeys.length === 0) return;

        const keysToLoad = processKeys.filter((item) => !loadedLogKeysRef.current.has(item.key));
        if (keysToLoad.length === 0) return;

        let cancelled = false;

        const loadInitialLogs = async () => {
            await Promise.all(keysToLoad.map(async (item) => {
                loadedLogKeysRef.current.add(item.key);
                try {
                    const result = await axiosInstance.post('/api/v1/get_server_process_logs', {
                        serverId: item.serverId,
                        processName: item.processName,
                        lines: INITIAL_LOG_LINES,
                    });
                    if (cancelled || !result.data?.flag) return;

                    const entries: Pm2LogMessage[] = result.data.logs ?? [];
                    if (entries.length === 0) return;

                    setLogs((prev) => {
                        if ((prev[item.key]?.length ?? 0) > 0) return prev;
                        return {
                            ...prev,
                            [item.key]: entries.map((entry, index) => ({
                                ...entry,
                                id: `${entry.serverId}-${entry.timestamp}-init-${index}`,
                            })),
                        };
                    });
                    if (pinnedToBottom.current[item.key] !== false) {
                        pinnedToBottom.current[item.key] = true;
                        pendingScrollKeys.current.add(item.key);
                    }
                } catch (error) {
                    console.error(`Failed to load log history for ${item.serverId}:`, error);
                }
            }));
        };

        loadInitialLogs();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance, configLoading, pm2Loading, processKeysSignature]);

    useEffect(() => {
        if (!authenticatedWebSocketUrl) return;

        const socket = new WebSocket(authenticatedWebSocketUrl);

        socket.onmessage = (event: MessageEvent) => {
            try {
                const log: Pm2LogMessage = JSON.parse(event.data);
                if (log.serverId && log.processName) {
                    appendLog(log);
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        return () => socket.close();
    }, [appendLog, authenticatedWebSocketUrl]);

    useLayoutEffect(() => {
        for (const [key, snapshot] of Object.entries(scrollSnapshots.current)) {
            const el = logRefs.current[key];
            if (!el || isPinned(key)) continue;

            const delta = el.scrollHeight - snapshot.scrollHeight;
            el.scrollTop = delta < 0
                ? Math.max(0, snapshot.scrollTop + delta)
                : snapshot.scrollTop;
        }
        scrollSnapshots.current = {};

        pendingScrollKeys.current.forEach((key) => {
            scrollPaneToBottom(key);
        });
        pendingScrollKeys.current.clear();
    }, [logs, scrollPaneToBottom, isPinned]);

    const logRefCallbacks = useRef<Record<string, (el: HTMLPreElement | null) => void>>({});
    const scrollCallbacks = useRef<Record<string, () => void>>({});
    const wheelCallbacks = useRef<Record<string, (event: React.WheelEvent<HTMLPreElement>) => void>>({});

    const getLogRef = useCallback((key: string) => {
        if (!logRefCallbacks.current[key]) {
            logRefCallbacks.current[key] = (el) => {
                logRefs.current[key] = el;
                if (!el) {
                    mountedPanes.current.delete(key);
                    return;
                }
                if (!mountedPanes.current.has(key)) {
                    mountedPanes.current.add(key);
                    pinnedToBottom.current[key] = true;
                    el.scrollTop = el.scrollHeight;
                }
            };
        }
        return logRefCallbacks.current[key];
    }, []);

    const getLogScrollHandler = useCallback((key: string) => {
        if (!scrollCallbacks.current[key]) {
            scrollCallbacks.current[key] = () => {
                const el = logRefs.current[key];
                if (el) {
                    const pinned = isScrolledToBottom(el);
                    setPinned(key, pinned);
                    if (pinned) {
                        trimLogsForKey(key);
                    }
                }
            };
        }
        return scrollCallbacks.current[key];
    }, [setPinned, trimLogsForKey]);

    const getLogWheelHandler = useCallback((key: string) => {
        if (!wheelCallbacks.current[key]) {
            wheelCallbacks.current[key] = (event) => {
                if (event.deltaY < 0) {
                    setPinned(key, false);
                    return;
                }
                const el = logRefs.current[key];
                if (el) {
                    window.requestAnimationFrame(() => {
                        const pinned = isScrolledToBottom(el);
                        setPinned(key, pinned);
                        if (pinned) {
                            trimLogsForKey(key);
                        }
                    });
                }
            };
        }
        return wheelCallbacks.current[key];
    }, [setPinned, trimLogsForKey]);

    const handleRestart = useCallback((serverId: string, processName: string) => {
        Modal.confirm({
            title: 'Restart PM2 process?',
            content: `This will restart "${processName}" on server "${serverId}". The bot may be briefly unavailable.`,
            okText: 'Restart',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const result = await axiosInstance.post('/api/v1/restart_server_process', { serverId, processName });
                    if (result.data?.flag) {
                        notifySuccess(`Restarted ${processName}`);
                    } else {
                        notifyError(result.data?.message || 'Restart failed');
                    }
                } catch {
                    notifyError('Restart failed');
                }
            },
        });
    }, [axiosInstance]);

    if (configLoading || pm2Loading) {
        return (
            <WithAuth>
                <MainWrapper>
                    <Spin tip="Loading..." />
                </MainWrapper>
            </WithAuth>
        );
    }

    if (processKeys.length === 0) {
        return (
            <WithAuth>
                <MainWrapper>
                    <Empty
                        style={{ padding: 48 }}
                        description="No bot servers or PM2 services configured"
                    />
                </MainWrapper>
            </WithAuth>
        );
    }

    return (
        <WithAuth>
            <MainWrapper>
                <div style={{ padding: PAGE_PADDING, boxSizing: 'border-box' }}>
                    <Row gutter={[GRID_GAP, GRID_GAP]}>
                        {processKeys.map((item) => (
                            <Col xs={24} sm={12} key={item.key}>
                                <div style={{ height: PANE_HEIGHT }}>
                                    <LogPane
                                        item={item}
                                        entries={logs[item.key] ?? []}
                                        logRef={getLogRef(item.key)}
                                        onLogScroll={getLogScrollHandler(item.key)}
                                        onLogWheel={getLogWheelHandler(item.key)}
                                        onRestart={handleRestart}
                                    />
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </MainWrapper>
        </WithAuth>
    );
};

export default BotLog;
